import React, { useState, useRef, useEffect, useCallback } from 'react';
import { apiClient } from '../../lib/api';
import { ChatMessage as ChatMessageType } from '../../types/chat';

interface InitialMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  criteria?: string[];
}

interface UseChatMessagesProps {
  tableId?: string;
  initialMessages?: InitialMessage[];
  defaultOpen?: boolean;
}

interface UseChatMessagesReturn {
  messages: ChatMessageType[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>;
  sessionId: string | null;
  setSessionId: (id: string | null) => void;
  isLoadingHistory: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  addFlowMessages: (newMessages: Array<{ id: string; role: 'user' | 'assistant'; content: string; timestamp: Date }>) => Promise<void>;
  getSessionId: () => string | null;
}

/**
 * Hook for managing chat messages state and history loading
 */
export function useChatMessages({
  tableId,
  initialMessages,
}: UseChatMessagesProps): UseChatMessagesReturn {
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Refs to prevent duplicate operations
  const hasLoadedHistory = useRef(false);
  const processedTableIdRef = useRef<string | null>(null);
  const initialMessagesRef = useRef(initialMessages);

  // Keep ref in sync with prop
  initialMessagesRef.current = initialMessages;

  // Initialize chat - always check backend, save initialMessages if needed
  useEffect(() => {
    if (!tableId) return;
    if (processedTableIdRef.current === tableId) return;

    const currentProcessingTableId = tableId;
    processedTableIdRef.current = tableId;

    const initializeChat = async () => {
      if (hasLoadedHistory.current) return;
      hasLoadedHistory.current = true;
      setIsLoadingHistory(true);

      try {
        // Always try to get existing session first
        const sessionData = await apiClient.getChatSessionByDataset(currentProcessingTableId);

        if (processedTableIdRef.current !== currentProcessingTableId) return;

        const currentInitialMessages = initialMessagesRef.current;

        if (sessionData?.messages?.length > 0) {
          // Session exists with messages - use backend data
          const chatMessages: ChatMessageType[] = sessionData.messages.map((msg: any) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at),
            actions: msg.actions || [],
            criteria: msg.criteria
          }));
          setMessages(chatMessages);
          setSessionId(sessionData.id);
        } else if (currentInitialMessages && currentInitialMessages.length > 0) {
          // No backend messages but we have initialMessages - display and save them
          const chatMessages: ChatMessageType[] = currentInitialMessages.map((msg, idx) => ({
            id: `initial-${currentProcessingTableId}-${idx}`,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.timestamp),
            actions: [],
            animate: msg.role === 'assistant' && !msg.criteria,
            criteria: msg.criteria
          }));
          setMessages(chatMessages);

          // Save initialMessages to backend
          const messagesToSave = currentInitialMessages.map(msg => ({
            role: msg.role,
            content: msg.content
          }));
          const lastMessage = messagesToSave[messagesToSave.length - 1];
          const history = messagesToSave.slice(0, -1);

          const result = await apiClient.sendChatMessage(
            lastMessage.content,
            { dataset_id: currentProcessingTableId },
            history,
            sessionData?.id || null,
            true
          );

          if (result.session_id) {
            setSessionId(result.session_id);
          }
        } else if (sessionData) {
          // Session exists but empty
          setSessionId(sessionData.id);
        }
      } catch (error) {
        console.error('Error initializing chat:', error);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    // Reset state
    setMessages([]);
    setSessionId(null);
    hasLoadedHistory.current = false;

    initializeChat();
  }, [tableId]);

  // Auto scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getSessionId = useCallback(() => sessionId, [sessionId]);

  // Handler to add messages from quick action flows and save them to backend
  const addFlowMessages = useCallback(async (
    newMessages: Array<{ id: string; role: 'user' | 'assistant'; content: string; timestamp: Date }>
  ) => {
    // Add messages to local state
    setMessages(prev => [...prev, ...newMessages.map(msg => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
    }))]);

    // Save to backend using save_only mode (fire and forget - don't block UI)
    try {
      const messagesToSave = newMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      // Use the last message as the main message, rest as history
      const lastMessage = messagesToSave[messagesToSave.length - 1];
      const history = messagesToSave.slice(0, -1);

      const result = await apiClient.sendChatMessage(
        lastMessage.content,
        { dataset_id: tableId },
        history,
        sessionId,
        true // saveOnly - skip LLM processing
      );
      // Update session ID if we got a new one
      if (result.session_id && !sessionId) {
        setSessionId(result.session_id);
      }
    } catch (error) {
      console.error('Failed to save flow messages:', error);
      // Don't show error to user - messages are still visible locally
    }
  }, [sessionId, tableId]);

  return {
    messages,
    setMessages,
    sessionId,
    setSessionId,
    isLoadingHistory,
    messagesEndRef,
    addFlowMessages,
    getSessionId,
  };
}
